import { hexToRgbArray } from '@movingimage-evp/mi-ui-component-library';
import { useEffect } from 'react';
import { useParams } from 'react-router';

import { useGetWebcastViewerQuery } from '../../generated/graphql-viewer';
import { type CssVariables, addCssVariables, addMetaProperty } from '../../utils';
import { useSelectedLanguage } from '../../viewer/providers/selected-language-provider';

export function ViewerCustomAppearance() {
  const { webcastId = '' } = useParams();
  const { selectedLanguage } = useSelectedLanguage();
  const { data } = useGetWebcastViewerQuery({ variables: { webcastId } });

  const { appearance, contents } = data?.webcast || {};

  useEffect(() => {
    const title = contents?.find(({ language }) => language === selectedLanguage?.code)?.title;

    if (title) {
      document.title = title;
      addMetaProperty('property', 'og:title', title);
    }

    if (selectedLanguage) addMetaProperty('property', 'og:locale', selectedLanguage.code);

    const {
      theme,
      primaryColor,
      backgroundColor,
      cardColor,
      headingFontDetails,
      headingFontColor,
      textFontDetails,
      textFontColor,
      logoUrl,
      faviconUrl,
    } = appearance || {};

    if (logoUrl) addMetaProperty('property', 'og:image', logoUrl);

    if (faviconUrl) {
      const links: NodeListOf<HTMLLinkElement> = document.querySelectorAll("link[rel*='icon']");
      links.forEach((link) => (link.href = faviconUrl));
    }

    if (!theme) return;

    document.documentElement.setAttribute('data-theme', theme.toLowerCase());

    const variables: CssVariables = {};
    const style = document.createElement('style');

    if (primaryColor) variables['--primaryColor'] = hexToRgbArray(primaryColor).join(',');
    if (primaryColor) document.documentElement.style.setProperty('--mi-brand-custom', primaryColor);
    if (backgroundColor) variables['--backgroundColor'] = hexToRgbArray(backgroundColor).join(',');
    if (cardColor) variables['--cardColor'] = hexToRgbArray(cardColor).join(',');
    if (headingFontColor) variables['--headingFontColor'] = hexToRgbArray(headingFontColor).join(',');
    if (textFontColor) variables['--textFontColor'] = hexToRgbArray(textFontColor).join(',');
    if (headingFontDetails?.id) variables['--headingFont'] = headingFontDetails.id;
    if (textFontDetails?.id) variables['--textFont'] = textFontDetails.id;

    if (headingFontDetails?.downloadLink) {
      variables['--headingFontDownloadLink'] = `url('${headingFontDetails.downloadLink}')`;

      style.textContent += `
        @font-face {
          font-family: '${headingFontDetails.id}';
          src: url('${headingFontDetails.downloadLink}');
          font-display: swap;
        }
      `;
    }

    if (textFontDetails?.downloadLink) {
      variables['--textFontDownloadLink'] = `url('${textFontDetails.downloadLink}')`;

      style.textContent += `
        @font-face {
          font-family: '${textFontDetails.id}';
          src: url('${textFontDetails.downloadLink}');
          font-display: swap;
        }
      `;
    }

    document.head.appendChild(style);
    addCssVariables(variables, theme);
  }, [appearance, contents, selectedLanguage]);

  return null;
}
